/**
 * Hazırlık (T05): every capability with its service, connection, data and policy state kept
 * apart, what Sercan can do about it and when Twin runs it next. The phone lists what is not
 * ready (all rows on request) and opens the facts on tap; the desktop rail shows the facts
 * inline. Levels: hazır, kısmen (service or data lag), seni bekliyor (Sercan's move), kapalı.
 * Audit 25 Oct 2026 (F6): with every row ready both views shrink to one line that links to
 * Kurulum; otherwise only the rows that need something show until "Tümü" is pressed.
 */

import { type ReactNode, useId, useState } from 'react';
import type { ReadinessFact, ReadinessItem, ReadinessLevel } from '../api/types.js';
import { Link } from '../app/router.js';
import { formatRelativeClock } from '../lib/format.js';
import { Icon } from './icons.js';
import { SectionHeader } from './primitives.js';

export const LEVEL_LABELS: Readonly<Record<ReadinessLevel, string>> = Object.freeze({
  ready: 'hazır',
  partial: 'kısmen',
  waiting: 'seni bekliyor',
  off: 'kapalı',
});

export function readyCount(items: readonly ReadinessItem[]): number {
  return items.filter((item) => item.level === 'ready').length;
}

function Fact({ fact }: { fact: ReadinessFact }) {
  return (
    <span className={fact.ok ? 'ready-fact ready-fact--ok' : 'ready-fact ready-fact--no'}>
      <span className="ready-fact__mark" aria-hidden="true">
        {fact.ok ? '✓' : '·'}
      </span>
      <span className="sr-only">{fact.ok ? 'tamam: ' : 'eksik: '}</span>
      {fact.label}
    </span>
  );
}

function facts(item: ReadinessItem, now: number): { key: string; label: string; body: ReactNode }[] {
  const rows = [
    { key: 'service', label: 'Servis', body: <Fact fact={item.service} /> },
    { key: 'connection', label: 'Bağlantı', body: <Fact fact={item.connection} /> },
    { key: 'data', label: 'Veri', body: <Fact fact={item.data} /> },
  ];
  if (item.policy) rows.push({ key: 'policy', label: 'Politika', body: <Fact fact={item.policy} /> });
  if (item.lastSuccessAt !== null) {
    rows.push({
      key: 'last',
      label: 'Son iş',
      body: <span className="mono">{formatRelativeClock(item.lastSuccessAt, now)}</span>,
    });
  }
  if (item.nextAt !== null) {
    rows.push({
      key: 'next',
      label: 'Sıradaki',
      body: <span className="mono">{formatRelativeClock(item.nextAt, now)}</span>,
    });
  }
  return rows;
}

function FactList({ id, item, now }: { id: string; item: ReadinessItem; now: number }) {
  return (
    <dl id={id} className="ready-facts">
      {facts(item, now).map((row) => (
        <div key={row.key} className="ready-facts__row">
          <dt>{row.label}</dt>
          <dd>{row.body}</dd>
        </div>
      ))}
    </dl>
  );
}

function ActionLink({ item }: { item: ReadinessItem }) {
  if (!item.action) return null;
  return (
    <Link to={item.action.to} className="ready-row__action" aria-label={`${item.title}: ${item.action.label}`}>
      {item.action.label}
      <Icon name="chevron-right" size={14} />
    </Link>
  );
}

/** "9/9 hazır › Kurulum": the whole block when nothing needs Sercan. */
function AllReady({ items, desktop = false }: { items: readonly ReadinessItem[]; desktop?: boolean }) {
  return (
    <Link
      to="/ayarlar/kurulum"
      className={desktop ? 'ready-line ready-line--desk' : 'ready-line'}
      aria-label={`Hazırlık: ${items.length} yeteneğin hepsi hazır. Kurulum ekranını aç`}
    >
      <span className="ready-row__dot ready-line__dot" aria-hidden="true" />
      <span className="ready-line__text">
        <span className="mono">
          {items.length}/{items.length}
        </span>{' '}
        hazır
      </span>
      <span className="ready-line__to">Kurulum</span>
      <Icon name="chevron-right" size={16} />
    </Link>
  );
}

/** Phone Bugün: the rows that need something, or every row; tap opens the facts. */
export function ReadinessSection({ items, now }: { items: readonly ReadinessItem[]; now: number }) {
  const baseId = useId();
  const [showAll, setShowAll] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const ready = readyCount(items);
  const pending = items.filter((item) => item.level !== 'ready');
  if (items.length > 0 && pending.length === 0) {
    return (
      <section aria-label="Hazırlık" className="section ready ready--all">
        <AllReady items={items} />
      </section>
    );
  }
  const shown = showAll ? items : pending;
  return (
    <section aria-labelledby="bugun-hazirlik" className="section ready">
      <SectionHeader
        id="bugun-hazirlik"
        title="Hazırlık"
        right={
          <span className="section-header__aside">
            <span className="mono">
              {ready}/{items.length}
            </span>{' '}
            hazır
          </span>
        }
      />
      <ul className="ready-list" aria-label="Yetenekler">
        {shown.map((item) => {
          const factsId = `${baseId}-${item.id}`;
          const isOpen = open === item.id;
          return (
            <li key={item.id} className={`ready-row ready-row--${item.level}`}>
              <button
                type="button"
                className="ready-row__main"
                aria-expanded={isOpen}
                aria-controls={isOpen ? factsId : undefined}
                onClick={() => setOpen(isOpen ? null : item.id)}
              >
                <span className="ready-row__dot" aria-hidden="true" />
                <span className="ready-row__title">{item.title}</span>
                <span className="ready-row__level">{LEVEL_LABELS[item.level]}</span>
                {item.note ? <span className="ready-row__note">{item.note}</span> : null}
              </button>
              <ActionLink item={item} />
              {isOpen ? <FactList id={factsId} item={item} now={now} /> : null}
            </li>
          );
        })}
      </ul>
      {pending.length > 0 ? (
        <button type="button" className="ready-toggle" aria-pressed={showAll} onClick={() => setShowAll(!showAll)}>
          {showAll ? 'Sadece eksikler' : `Tümü (${items.length})`}
        </button>
      ) : null}
    </section>
  );
}

/** Desktop rail: the rows that need something (every row on request) with their facts inline. */
export function ReadinessPanel({ items, now }: { items: readonly ReadinessItem[]; now: number }) {
  const [showAll, setShowAll] = useState(false);
  const ready = readyCount(items);
  const pending = items.filter((item) => item.level !== 'ready');
  if (items.length > 0 && pending.length === 0) {
    return (
      <section className="panel" aria-label="Hazırlık">
        <AllReady items={items} desktop />
      </section>
    );
  }
  const shown = showAll ? items : pending;
  return (
    <section className="panel" aria-labelledby="h-hazirlik">
      <SectionHeader
        id="h-hazirlik"
        title="Hazırlık"
        className="panel__head"
        right={
          <span className="panel__aside">
            <span className="mono">
              {ready}/{items.length}
            </span>{' '}
            hazır
          </span>
        }
      />
      <ul className="ready-list ready-list--desk" aria-label="Yetenekler">
        {shown.map((item) => (
          <li key={item.id} className={`ready-row ready-row--${item.level}`}>
            <div className="ready-row__head">
              <span className="ready-row__dot" aria-hidden="true" />
              <span className="ready-row__title">{item.title}</span>
              <span className="ready-row__level">{LEVEL_LABELS[item.level]}</span>
              <ActionLink item={item} />
            </div>
            <div className="ready-row__facts">
              {facts(item, now).map((row) => (
                <span key={row.key} className="ready-row__fact">
                  <span className="ready-row__fact-label">{row.label}</span> {row.body}
                </span>
              ))}
            </div>
          </li>
        ))}
      </ul>
      <button
        type="button"
        className="ready-toggle ready-toggle--desk"
        aria-pressed={showAll}
        onClick={() => setShowAll(!showAll)}
      >
        {showAll ? 'Sadece eksikler' : `Tümü (${items.length})`}
      </button>
    </section>
  );
}
